import React, { useId } from "react";
import { cn } from "../../lib/cn";

/**
 * Input
 * @param {string} [label]
 * @param {string} [placeholder]
 * @param {string} [error] - error message; shows red border + message when set
 * @param {string} [helperText]
 * @param {React.ReactNode} [leftIcon]
 * @param {React.ReactNode} [rightIcon]
 * @param {boolean} [disabled]
 * @param {string} [type="text"]
 * @param {string} [className]
 * @param {string} [id]
 */
export const Input = React.forwardRef(function Input(
  {
    label,
    placeholder,
    error,
    helperText,
    leftIcon,
    rightIcon,
    disabled = false,
    type = "text",
    className,
    id,
    ...props
  },
  ref
) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="mb-1.5 block text-sm font-medium text-neutral-900 dark:text-neutral-100"
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-400 dark:text-neutral-500">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : helperText ? helperId : undefined}
          className={cn(
            "h-10 w-full rounded border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 transition-colors",
            "outline-none focus:border-primary-300 focus-visible:ring-2 focus-visible:ring-primary-200 dark:focus-visible:ring-primary-500/40 dark:focus-visible:ring-offset-neutral-900",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-50 dark:disabled:bg-neutral-800",
            leftIcon && "pl-9",
            rightIcon && "pr-9",
            error && "border-danger-500 focus:border-danger-500 focus-visible:ring-danger-500",
            className
          )}
          {...props}
        />
        {rightIcon && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 dark:text-neutral-500">
            {rightIcon}
          </span>
        )}
      </div>
      {error ? (
        <p id={errorId} className="mt-1.5 text-sm text-danger-700 dark:text-danger-500">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          {helperText}
        </p>
      ) : null}
    </div>
  );
});